import styled from '@emotion/styled';
import Container from '@ep/components/Container';
import Media from '@ep/components/Media';
import { Body } from '@ep/templates/ProductPage';
import theme from '@ep/theme';
import getHtmlProp from '@ep/utils/getHtmlProp';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import React from 'react';
import {
  DescriptionProps,
  GridProps,
  MixedMediaProps,
} from './MixedMediaProps';

// grid-area: row-start / column-start / row-end / column-end
const Grid = styled.div<GridProps>`
  .backgroundColor{
    padding: 0.1rem 0.8rem 0.8rem 1rem;
    border-radius: 0.5rem;
    background-color: ${({subBoxColor})=>subBoxColor ? subBoxColor : theme.colors.backgroundBlue }; 
  }
  .strike {
    strong {
      text-decoration: line-through;
    }
    height: 3rem;
  }
  display: grid;
  grid-template-columns: ${({ sideElements }) =>
    sideElements ? `6fr 4fr` : `1fr`};
  grid-template-row: auto auto;
  gap: 1rem 6rem;
  margin: 3rem 0;
  .column-1 {
    grid-area: 1 / 1;
  }
  .column-2 {
    grid-area: ${({ mainMedia }) => (mainMedia ? `2 / 1` : `1 / 1`)};
  }
  .column-3 {
    grid-area: 2 / 2;
  }
  .column-4 {
    grid-area: 1 / 2;
  }
  ${theme.mediaQueries.mobileAndTablet} {
    grid-template-columns: 1fr;
    grid-template-row: auto auto auto auto;
    margin: 2rem 0;
    gap: 0px;
    .column-1 {
      grid-area: 1;
    }
    .column-2 {
      grid-area: 2;
    }
    .column-3 {
      grid-area: 3;
    }
    .column-4 {
      grid-area: 4;
    }
  }
`;

const Column = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
`;

const MediaContainer = styled.div`
  width: 100%;
  margin-left: 0.5rem;
  margin-top: 0.5rem;
`;

const Description = styled.div<DescriptionProps>`
  width: 100%;
  position: relative;
  padding: 7px;
  p {
    margin: 1rem 0;
  }
  p:empty::before {
    content: '​';
  }
  ul {
    padding: 0;
    margin: 0;
    li {
      list-style: none;
      padding-left: 1.5rem;
      position: relative;
      margin-bottom: 0.2rem;
      &:before {
        content: '>';
        position: absolute;
        left: 0;
        top: 0;
        color: ${theme.colors.oceanBlue};
      }
    }
  }
  ${({ textCenter }) =>
    textCenter
      ? `
        text-align: center;
        p {
          text-align: left;
        }
      `
      : ``}
`;

const StyledMedia = styled(Media)`
  width: 100%;
`;

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  ${theme.mediaQueries.mobile} {
    justify-content: center;
    align-items: center;
  }
`;

const CloseModal = styled.div`
  position: relative;
  z-index: 3;
`;

const ModalContainer = styled(Container)`
  max-height: 95vh;
  width: 120vh;
  background-color: ${theme.colors.white};
  outline: none;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  h1 {
    color: ${theme.colors.duskBlue};
    font-family: ${theme.typography.primaryFont.regular};
    line-height: 1.4em;
  }
  ${theme.mediaQueries.tablet} {
    h1 {
      color: ${theme.colors.duskBlue};
      font-family: ${theme.typography.primaryFont.regular};
      font-size: 1.5em;
      line-height: 1.5em;
    }
    p {
      font-size: 1em;
    }
    width: 42rem;
    margin: 0;
  }
  ${theme.mediaQueries.mobile} {
    h1 {
      color: ${theme.colors.duskBlue};
      font-family: ${theme.typography.primaryFont.regular};
      font-size: 1.2em;
      line-height: 1.2em;
    }
    p {
      font-size: 0.9em;
    }
    width: 20rem;
    margin: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  padding-bottom: 0rem;
`;

const LightboxMedia = styled(Media)`
  flex: 1;
  margin-bottom: 2rem;
`;

const MixedMediaModule = ({
  mainMedia,
  mainDescription,
  sideMedia,
  sideMediaThumbnail,
  sideDescription,
  sDSBColor,
}: MixedMediaProps) => {
  const [open, setOpen] = React.useState(false);

  const HandleClick = () => {
    //dataLayer.push({
    //  event:
    //    'Page Interaction - user clicked on card -  ' + interactiveCard.title,
    //});
    setOpen(true);
  };
  return (
    <Container>
      <Grid
        mainMedia={Boolean(mainMedia)}
        sideElements={Boolean(sideDescription)}
        subBoxColor={sDSBColor ? sDSBColor.hex : undefined}
      >
        <Column className="column-1">
          <MediaContainer>
            <StyledMedia media={mainMedia} lazy cover />
          </MediaContainer>
        </Column>
        <Column className="column-2">
          <Description>
            <Body {...getHtmlProp(mainDescription.html)} /> 
          </Description>
        </Column>
        <Column className="column-3">
          <MediaContainer>
            <LightboxMedia
              media={sideMediaThumbnail}
              lazy
              onClick={() => (sideMedia ? HandleClick() : null)}
              style={{ cursor: sideMedia ? 'pointer' : 'default' }}
            />
          </MediaContainer>
        </Column>
        <Column className="column-4">
          <Description>
            <Body {...getHtmlProp(sideDescription.html)} />  
          </Description>
        </Column>
      </Grid>
      <StyledModal open={open} onClose={() => setOpen(false)}>
        <ModalContainer>
          <ModalHeader>
            <CloseModal>
              <IconButton
                aria-label="close"
                onClick={() => setOpen(false)}
                style={{ backgroundColor: theme.colors.white85 }}
              >
                <CloseIcon htmlColor={theme.colors.black} />
              </IconButton>
            </CloseModal>
          </ModalHeader>
          {sideMedia && <StyledMedia media={sideMedia} />}
        </ModalContainer>
      </StyledModal>
    </Container>
  );
};

export default React.memo(MixedMediaModule);

{
  /* <StyledContainer reverse={reverse}>
{media && (
  <MediaContainer reverse={reverse}>
    <StyledMedia media={media} lazy />
  </MediaContainer>
)}
{bodyContent && (
  <Description reverse={reverse} center={center}>
    <Body {...getHtmlProp(bodyContent.html)} />
  </Description>
)}
</StyledContainer> */
}
